import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { auditorService } from '../../services/appwriteService';
import { authService } from '../../services/appwriteService';
import ModernHeader from './components/ModernHeader';
import ModernSidebar from './components/ModernSidebar';
import EnhancedOverview from './views/EnhancedOverview';
import UsersView from './views/UsersView';
import SessionsView from './views/SessionsView';
import TasksView from './views/TasksView';
import ReportsView from './views/ReportsView';
import TotalTasksView from './views/TotalTasksView'; // NOUVEAU IMPORT
import CreateUserModal from './components/CreateUserModal';

// Import des hooks optimisés
import { useOptimizedDashboardData } from './hooks/useOptimizedDashboardData';

// Import des types
import { NewUser, AdvancedFilters } from './types/AdminTypes';

// 🎯 PRESETS DE FILTRES PAR VUE - MÉMORISÉS avec NOUVEAU preset totaltasks
const getFilterPresets = () => {
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const week = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const month = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  return {
    overview: {
      default: { from: month, to: today, label: "30 derniers jours" },
      presets: [
        { from: today, to: today, label: "Aujourd'hui" },
        { from: week, to: today, label: "7 derniers jours" },
        { from: month, to: today, label: "30 derniers jours" },
        { from: '', to: '', label: "Toutes les données" }
      ]
    },
    sessions: {
      default: { from: today, to: today, label: "Temps réel (Aujourd'hui)" },
      presets: [
        { from: today, to: today, label: "🔴 Temps réel (Aujourd'hui)" },
        { from: yesterday, to: yesterday, label: "Hier" },
        { from: week, to: today, label: "7 derniers jours" },
        { from: month, to: today, label: "30 derniers jours" }
      ]
    },
    tasks: {
      default: { from: week, to: today, label: "7 derniers jours" },
      presets: [
        { from: today, to: today, label: "Aujourd'hui" },
        { from: week, to: today, label: "7 derniers jours" },
        { from: month, to: today, label: "30 derniers jours" },
        { from: '', to: '', label: "Toutes les données" }
      ]
    },
    users: {
      default: { from: '', to: '', label: "Tous les utilisateurs" },
      presets: [
        { from: '', to: '', label: "Tous les utilisateurs" },
        { from: month, to: today, label: "Actifs (30 derniers jours)" }
      ]
    },
    reports: {
      default: { from: month, to: today, label: "30 derniers jours" },
      presets: [
        { from: today, to: today, label: "Aujourd'hui" },
        { from: week, to: today, label: "7 derniers jours" },
        { from: month, to: today, label: "30 derniers jours" },
        { from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], to: today, label: "90 derniers jours" }
      ]
    },
    // NOUVEAU PRESET pour Total Tasks
    totaltasks: {
      default: { from: week, to: today, label: "7 derniers jours" },
      presets: [
        { from: today, to: today, label: "Aujourd'hui" },
        { from: week, to: today, label: "7 derniers jours" },
        { from: month, to: today, label: "30 derniers jours" },
        { from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], to: today, label: "90 derniers jours" }
      ]
    }
  };
};

const ModernAdminDashboard = () => {
  const { signOut, profile } = useAuth();
  const [currentView, setCurrentView] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // États pour la création d'utilisateur
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState<NewUser>({
    email: '',
    password: '',
    full_name: '',
    role: 'auditor'
  });

  // ✅ FILTRES - Fonction mémorisée pour éviter les re-créations
  const getDefaultFilters = useCallback((view: string): AdvancedFilters => {
    const presets = getFilterPresets();
    const preset = presets[view as keyof typeof presets]?.default || presets.overview.default;
    
    return {
      dateFrom: preset.from,
      dateTo: preset.to,
      auditorId: '',
      agentEmail: '',
      taskType: '',
      hasComments: false,
      sessionStatus: '',
      minTasksPerDay: 0,
      maxTasksPerDay: 1000
    };
  }, []);

  const [filters, setFilters] = useState<AdvancedFilters>(() => getDefaultFilters('overview'));
  const [activePreset, setActivePreset] = useState<string>('default');

  // ✅ HOOK OPTIMISÉ - Un seul hook pour toutes les données
  const {
    globalStats,
    auditorStats,
    sessions,
    taskDetails,
    users,
    loading,
    error,
    loadingStages,
    refetchData,
    refetchUsers
  } = useOptimizedDashboardData(filters);

  // ✅ MÉMORISER les presets pour éviter les re-créations
  const filterPresets = useMemo(() => getFilterPresets(), []);

  // ✅ CHANGEMENT DE VUE - Réinitialiser les filtres SEULEMENT si nécessaire
  useEffect(() => {
    console.log(`🎯 Changement de vue vers: ${currentView}`);
    const newFilters = getDefaultFilters(currentView);
    
    // ✅ ÉVITER LA BOUCLE : Comparer les filtres avant de les changer
    const hasChanged = JSON.stringify(newFilters) !== JSON.stringify(filters);
    
    if (hasChanged) {
      setFilters(newFilters);
      setActivePreset('default');
      console.log(`🔄 Filtres mis à jour pour ${currentView}`);
    }
  }, [currentView, getDefaultFilters]);

  // ✅ FONCTIONS DE CALLBACK MÉMORISÉES
  const applyDatePreset = useCallback((preset: { from: string; to: string; label: string }) => {
    console.log(`📅 Application preset: ${preset.label}`);
    setFilters(prevFilters => ({
      ...prevFilters,
      dateFrom: preset.from,
      dateTo: preset.to
    }));
    setActivePreset(preset.label);
  }, []);

  const applyCustomDates = useCallback((dateFrom: string, dateTo: string) => {
    console.log('🎨 Application dates personnalisées:', { dateFrom, dateTo });
    setFilters(prevFilters => ({
      ...prevFilters,
      dateFrom,
      dateTo
    }));
    setActivePreset('custom');
  }, []);

  // ✅ CRÉATION D'UTILISATEUR
  const handleCreateUser = useCallback(async () => {
    try {
      console.log('🚀 Tentative création utilisateur:', newUser);
      
      if (!newUser.email || !newUser.password || !newUser.full_name) {
        alert('Veuillez remplir tous les champs obligatoires');
        return;
      }

      const createdUser = await authService.createUser(
        newUser.email, 
        newUser.password, 
        newUser.full_name
      );
      
      console.log('✅ Utilisateur créé:', createdUser);
      
      setShowCreateUser(false);
      setNewUser({
        email: '',
        password: '',
        full_name: '',
        role: 'auditor'
      });
      
      await refetchUsers();
      
      setTimeout(() => {
        alert('Utilisateur créé avec succès !');
      }, 100);
      
    } catch (error: any) {
      console.error('❌ Erreur création utilisateur:', error);
      alert(`Erreur: ${error.message}`);
    }
  }, [newUser, refetchUsers]);

  const handleCloseModal = useCallback(() => {
    setShowCreateUser(false);
    setNewUser({ email: '', password: '', full_name: '', role: 'auditor' });
  }, []);

  // ✅ CALLBACK POUR CHANGER LES FILTRES (évite les re-renders inutiles)
  const handleFiltersChange = useCallback((newFilters: AdvancedFilters) => {
    console.log('🔧 Mise à jour des filtres:', newFilters);
    setFilters(newFilters);
  }, []);

  // ✅ AFFICHAGE DU LOADER AVEC ÉTAPES
  if (loading.initial) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="relative mb-8">
            <div className="w-16 h-16 sm:w-24 sm:h-24 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          </div>
          
          <p className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
            Chargement du dashboard...
          </p>
          
          {/* Étapes de chargement */}
          <div className="space-y-2 text-sm text-gray-600">
            <div className={`flex items-center justify-center space-x-2 ${loadingStages.users ? 'text-green-600' : loadingStages.isLoadingUsers ? 'text-blue-600' : 'text-gray-400'}`}>
              {loadingStages.users ? '✅' : loadingStages.isLoadingUsers ? '⏳' : '⏸️'}
              <span>Utilisateurs</span>
            </div>
            
            <div className={`flex items-center justify-center space-x-2 ${loadingStages.globalStats ? 'text-green-600' : loadingStages.isLoadingGlobalStats ? 'text-blue-600' : 'text-gray-400'}`}>
              {loadingStages.globalStats ? '✅' : loadingStages.isLoadingGlobalStats ? '⏳' : '⏸️'}
              <span>Statistiques globales</span>
            </div>
            
            <div className={`flex items-center justify-center space-x-2 ${loadingStages.auditorStats ? 'text-green-600' : loadingStages.isLoadingAuditorStats ? 'text-blue-600' : 'text-gray-400'}`}>
              {loadingStages.auditorStats ? '✅' : loadingStages.isLoadingAuditorStats ? '⏳' : '⏸️'}
              <span>Données auditeurs</span>
            </div>
            
            <div className={`flex items-center justify-center space-x-2 ${loadingStages.sessions ? 'text-green-600' : loadingStages.isLoadingSessions ? 'text-blue-600' : 'text-gray-400'}`}>
              {loadingStages.sessions ? '✅' : loadingStages.isLoadingSessions ? '⏳' : '⏸️'}
              <span>Sessions actives</span>
            </div>
          </div>
          
          <p className="text-xs text-gray-500 mt-4">
            Optimisation en cours... {Math.round((Object.values(loadingStages).filter(Boolean).length / 4) * 100)}%
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-red-700 mb-2">Erreur de chargement</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={refetchData}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30">
      <div className="flex">
        <ModernSidebar 
          currentView={currentView}
          setCurrentView={setCurrentView}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
        
        <div className="flex-1 lg:ml-0">
          <ModernHeader 
            profile={profile}
            signOut={signOut}
            setSidebarOpen={setSidebarOpen}
            onRefresh={refetchData}
          />
          
          <main className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-none lg:max-w-7xl lg:mx-auto">
            {currentView === 'overview' && (
              <EnhancedOverview 
                globalStats={globalStats}
                auditorStats={auditorStats}
                sessions={sessions}
              />
            )}
            
            {currentView === 'users' && (
              <UsersView 
                users={users}
                setShowCreateUser={setShowCreateUser}
                onToggleUserStatus={refetchUsers}
              />
            )}
            
            {currentView === 'sessions' && (
              <SessionsView 
                sessions={sessions}
                taskDetails={taskDetails}
                filters={filters}
                setFilters={handleFiltersChange}
                auditorStats={auditorStats}
                filterPresets={filterPresets.sessions}
                activePreset={activePreset}
                onApplyPreset={applyDatePreset}
                onApplyCustomDates={applyCustomDates}
              />
            )}
            
            {currentView === 'tasks' && (
              <TasksView 
                taskDetails={taskDetails}
                filters={filters}
                setFilters={handleFiltersChange}
                users={users}
                filterPresets={filterPresets.tasks}
                activePreset={activePreset}
                onApplyPreset={applyDatePreset}
                onApplyCustomDates={applyCustomDates}
              />
            )}
            
            {currentView === 'reports' && (
              <ReportsView 
                globalStats={globalStats}
                auditorStats={auditorStats}
                filters={filters}
                filterPresets={filterPresets.reports}
                activePreset={activePreset}
                onApplyPreset={applyDatePreset}
                onApplyCustomDates={applyCustomDates}
              />
            )}

            {/* NOUVEAU - Total Tasks View */}
            {currentView === 'totaltasks' && (
              <TotalTasksView />
            )}
          </main>
        </div>
      </div>

      <CreateUserModal
        show={showCreateUser}
        onClose={handleCloseModal}
        newUser={newUser}
        setNewUser={setNewUser}
        onSubmit={handleCreateUser}
      />
    </div>
  );
};

export default ModernAdminDashboard;